import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { alpha, useTheme } from '@mui/material/styles';

import { formatEther } from 'viem';
import { usePublicClient } from 'wagmi';

import Iconify from 'src/components/iconify';
import { TARGET_CHAIN_ID } from 'src/config/wagmiConfig';
import { ETH_DEPOSIT_GAS_LIMIT, getDepositFees } from 'src/utils/crypto/depositFees';
import { useSelector } from 'src/redux/hooks';
import { clientDispatch } from 'src/redux/clientStore';
import { fetchProfitPercent } from 'src/redux/slices/client-profit-slice';

import { UI, PAYOUT_PROVIDERS } from '../constants';
import { MonoLabel } from './atoms';

// ----------------------------------------------------------------------
// Assumptions — all in one place so they are easy to adjust.
// ----------------------------------------------------------------------

// Buying ETH in MetaMask via Revolut Ramp (card / Google Pay).
// Observed: €2.45 fee on a €100 order (2026-10-15).
const RAMP_FEE_PCT = 0.0245;

// Gas actually used by depositETH (observed 62,235 on mainnet).
const ETH_DEPOSIT_GAS_USED = 65_000n;

// Must match MAX_RESERVE_FEE_MULTIPLIER in useTokenOneMax: MAX keeps this
// much ETH back for gas, so the calculator models a MAX send.
const MAX_RESERVE_FEE_MULTIPLIER = 150n;

// Used if live gas cannot be read (values from a real mainnet deposit).
const GAS_FALLBACK = { gasPaidEth: 0.00013, reserveEth: 0.0005 };

const REFRESH_MS = 60_000;
const PRICE_URL =
  'https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd,eur';

const PRESETS = [50, 100, 500, 1000];

// ----------------------------------------------------------------------
// Data helpers
// ----------------------------------------------------------------------
async function fetchEthPrices() {
  const res = await fetch(PRICE_URL);
  if (!res.ok) throw new Error(`Price fetch failed: ${res.status}`);

  const data = await res.json();
  const usd = Number(data?.ethereum?.usd);
  const eur = Number(data?.ethereum?.eur);

  if (!Number.isFinite(usd) || usd <= 0) throw new Error('ETH/USD price missing');
  if (!Number.isFinite(eur) || eur <= 0) throw new Error('ETH/EUR price missing');

  return { usd, eur };
}

async function fetchGasEstimate(publicClient) {
  const { maxFeePerGas, maxPriorityFeePerGas } = await getDepositFees(publicClient);

  // maxFee = 2 × baseFee + tip  →  what is actually paid = baseFee + tip
  const baseFee = (maxFeePerGas - maxPriorityFeePerGas) / 2n;
  const effectiveFeePerGas = baseFee + maxPriorityFeePerGas;

  const gasPaidWei = ETH_DEPOSIT_GAS_USED * effectiveFeePerGas;
  const reserveWei = (ETH_DEPOSIT_GAS_LIMIT * maxFeePerGas * MAX_RESERVE_FEE_MULTIPLIER) / 100n;

  return {
    gasPaidEth: Number(formatEther(gasPaidWei)),
    reserveEth: Number(formatEther(reserveWei)),
  };
}

function computeQuote({ amount, currency, prices, gas, feeRate }) {
  if (!Number.isFinite(amount) || amount <= 0) return null;
  if (!prices || !gas) return null;

  const ethPrice = prices[currency];
  if (!ethPrice) return null;

  // What the amount is worth in USD at market rate (no fees).
  const marketUsd = (amount / ethPrice) * prices.usd;

  const ethBought = (amount * (1 - RAMP_FEE_PCT)) / ethPrice;
  const depositEth = ethBought - gas.reserveEth;

  if (depositEth <= 0) return { tooSmall: true };

  const depositUsd = depositEth * prices.usd;
  const recipientUsd = Math.floor(depositUsd * (1 - feeRate) * 100) / 100;

  let leftoverEth = gas.reserveEth - gas.gasPaidEth;
  if (leftoverEth < 0) leftoverEth = 0;
  const leftoverUsd = leftoverEth * prices.usd;

  // Everything lost along the way: Revolut + gas + SafiSwap.
  const totalFeesUsd = marketUsd - recipientUsd - leftoverUsd;

  return {
    tooSmall: false,
    marketUsd,
    totalFeesUsd,
    totalFeesPct: (totalFeesUsd / marketUsd) * 100,
    recipientUsd,
    leftoverUsd,
  };
}

function formatUsd(value) {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function getCurrencySymbol(currency) {
  if (currency === 'usd') return '$';
  return '€';
}

// ----------------------------------------------------------------------
// Styles — same language as the landing page: pills, mono eyebrows,
// gradient CTA, green-edged panel.
// ----------------------------------------------------------------------
const paperSx = {
  bgcolor: { xs: UI.bg, sm: UI.panel },
  backgroundImage: 'none',
  color: UI.text,
  border: { xs: 0, sm: `1px solid ${alpha(UI.green, 0.5)}` },
  borderRadius: { xs: 0, sm: 3 },
  boxShadow: {
    xs: 'none',
    sm: `0 24px 70px rgba(0,0,0,0.55), 0 0 40px ${alpha(UI.green, 0.12)}`,
  },
};

const fieldSx = {
  borderRadius: 2,
  border: `1px solid ${UI.border}`,
  bgcolor: UI.field,
  px: 2,
  py: 1.75,
  transition: 'border-color 0.15s ease',
  '&:focus-within': { borderColor: UI.green },
};

function getToggleSx(active) {
  const base = {
    flex: 1,
    textAlign: 'center',
    py: 0.85,
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    userSelect: 'none',
    transition: 'all 0.15s ease',
  };

  if (active) {
    return { ...base, color: '#0A0B08', background: UI.gradient };
  }

  return { ...base, color: UI.textMuted, '&:hover': { color: UI.text } };
}

function getPresetSx(active) {
  const base = {
    flex: 1,
    textAlign: 'center',
    py: 0.75,
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    userSelect: 'none',
    transition: 'all 0.15s ease',
  };

  if (active) {
    return {
      ...base,
      color: UI.green,
      border: `1px solid ${alpha(UI.green, 0.6)}`,
      bgcolor: alpha(UI.green, 0.12),
    };
  }

  return {
    ...base,
    color: UI.textMuted,
    border: `1px solid ${UI.border}`,
    bgcolor: 'transparent',
    '&:hover': { borderColor: UI.borderStrong, color: UI.text },
  };
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
export default function CalculatorDialog({ open, onClose, onSendNow }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const publicClient = usePublicClient({ chainId: TARGET_CHAIN_ID });

  const profitPercentRaw = useSelector((state) => state?.settings?.profitPercent);
  const profitLoaded = profitPercentRaw !== null && profitPercentRaw !== undefined;

  const [currency, setCurrency] = useState('eur');
  const [amountInput, setAmountInput] = useState('100');
  const [prices, setPrices] = useState(null);
  const [priceError, setPriceError] = useState('');
  const [gas, setGas] = useState(null);

  useEffect(() => {
    if (!open || profitLoaded) return;
    clientDispatch(fetchProfitPercent());
  }, [open, profitLoaded]);

  // Only poll prices and gas while the dialog is open.
  useEffect(() => {
    if (!open) return undefined;

    let ignore = false;

    async function refresh() {
      try {
        const nextPrices = await fetchEthPrices();
        if (!ignore) {
          setPrices(nextPrices);
          setPriceError('');
        }
      } catch (error) {
        console.error('[CalculatorDialog] price fetch failed', error);
        // Keep the last good prices; only surface an error if we have none.
        if (!ignore) setPriceError('Live rates are unavailable right now.');
      }

      if (!publicClient) {
        if (!ignore) setGas(GAS_FALLBACK);
        return;
      }

      try {
        const nextGas = await fetchGasEstimate(publicClient);
        if (!ignore) setGas(nextGas);
      } catch (error) {
        console.error('[CalculatorDialog] gas estimate failed', error);
        if (!ignore) setGas(GAS_FALLBACK);
      }
    }

    refresh();
    const timer = setInterval(refresh, REFRESH_MS);

    return () => {
      ignore = true;
      clearInterval(timer);
    };
  }, [open, publicClient]);

  const feeRate = useMemo(() => {
    const pct = Number(profitPercentRaw);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    return pct / 100;
  }, [profitPercentRaw]);

  const amount = Number.parseFloat(amountInput);

  const quote = useMemo(() => {
    if (!profitLoaded) return null;
    return computeQuote({ amount, currency, prices, gas, feeRate });
  }, [amount, currency, prices, gas, feeRate, profitLoaded]);

  const handleAmountChange = (event) => {
    const { value } = event.target;
    if (value === '') {
      setAmountInput('');
      return;
    }
    if (!/^\d*(\.\d{0,2})?$/.test(value)) return;
    setAmountInput(value);
  };

  // ---- render helpers --------------------------------------------------
  function renderHeader() {
    return (
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: UI.green }} />
            <Typography
              sx={{
                fontFamily: UI.mono,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: UI.green,
              }}
            >
              Remittance calculator
            </Typography>
          </Stack>
          <Typography
            sx={{
              mt: 1,
              fontSize: { xs: 24, sm: 26 },
              fontWeight: 800,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              color: UI.text,
            }}
          >
            How much will they receive?
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: 13.5, color: UI.textMuted }}>
            For ETH bought in MetaMask by card or Google Pay. Every fee included, updated every
            minute.
          </Typography>
        </Box>

        <IconButton
          onClick={onClose}
          aria-label="Close calculator"
          sx={{
            width: 38,
            height: 38,
            flexShrink: 0,
            borderRadius: 6,
            color: UI.textMuted,
            border: `1px solid ${UI.border}`,
            '&:hover': { color: UI.text, borderColor: UI.borderStrong },
          }}
        >
          <Iconify icon="mingcute:close-line" width={20} />
        </IconButton>
      </Stack>
    );
  }

  function renderCurrencyToggle() {
    return (
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ p: 0.5, borderRadius: 6, border: `1px solid ${UI.border}`, bgcolor: UI.bg }}
      >
        <Box sx={getToggleSx(currency === 'eur')} onClick={() => setCurrency('eur')}>
          EUR €
        </Box>
        <Box sx={getToggleSx(currency === 'usd')} onClick={() => setCurrency('usd')}>
          USD $
        </Box>
      </Stack>
    );
  }

  function renderPresets() {
    return (
      <Stack direction="row" spacing={0.75} sx={{ mt: 1.5 }}>
        {PRESETS.map((preset) => (
          <Box
            key={preset}
            onClick={() => setAmountInput(String(preset))}
            sx={getPresetSx(amount === preset)}
          >
            {getCurrencySymbol(currency)}
            {preset}
          </Box>
        ))}
      </Stack>
    );
  }

  function renderAmountField() {
    return (
      <Box sx={fieldSx}>
        <MonoLabel>You spend in MetaMask</MonoLabel>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
          <Typography
            sx={{ fontSize: 34, fontWeight: 800, color: UI.textFaint, letterSpacing: '-0.02em' }}
          >
            {getCurrencySymbol(currency)}
          </Typography>
          <InputBase
            value={amountInput}
            onChange={handleAmountChange}
            placeholder="0.00"
            inputProps={{ inputMode: 'decimal', 'aria-label': 'Amount to spend' }}
            fullWidth
            sx={{
              fontSize: 34,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: UI.text,
              '& input::placeholder': { color: UI.textFaint, opacity: 0.6 },
            }}
          />
        </Stack>
        {renderPresets()}
      </Box>
    );
  }

  function renderRow(label, value, hint) {
    return (
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Box>
          <Typography sx={{ fontSize: 13.5, color: UI.textMuted }}>{label}</Typography>
          {hint && (
            <Typography sx={{ fontFamily: UI.mono, fontSize: 10.5, color: UI.textFaint }}>
              {hint}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: UI.text, whiteSpace: 'nowrap' }}>
          {value}
        </Typography>
      </Stack>
    );
  }

  function renderStatus(text, color) {
    return (
      <Box
        sx={{
          borderRadius: 2,
          border: `1px dashed ${UI.border}`,
          py: 4,
          px: 2,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 13.5, color }}>{text}</Typography>
      </Box>
    );
  }

  function renderProviders() {
    return (
      <Stack direction="row" spacing={0.75} justifyContent="center" flexWrap="wrap" useFlexGap>
        {PAYOUT_PROVIDERS.map((provider) => (
          <Box
            key={provider.key}
            sx={{
              px: 1.25,
              py: 0.4,
              borderRadius: 5,
              border: `1px solid ${alpha(UI.green, 0.25)}`,
              fontSize: 11.5,
              fontWeight: 600,
              color: UI.green,
            }}
          >
            {provider.label}
          </Box>
        ))}
      </Stack>
    );
  }

  function renderResult() {
    if (!quote && !prices && priceError) return renderStatus(priceError, UI.yellow);
    if (!quote && (!prices || !gas || !profitLoaded)) {
      return renderStatus('Fetching live rates…', UI.textMuted);
    }
    if (!quote) return renderStatus('Enter an amount to see what they receive.', UI.textMuted);
    if (quote.tooSmall) {
      return renderStatus('Amount too small to cover the network fee.', UI.danger);
    }

    return (
      <Stack spacing={2}>
        <Stack spacing={1.25} sx={{ px: 0.5 }}>
          {renderRow('Market value', formatUsd(quote.marketUsd))}
          <Box sx={{ borderTop: `1px dashed ${UI.border}` }} />
          {renderRow(
            `Total fees (${quote.totalFeesPct.toFixed(1)}%)`,
            `− ${formatUsd(quote.totalFeesUsd)}`,
            'MetaMask card purchase + network + SafiSwap'
          )}
        </Stack>

        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2.5,
            border: `1px solid ${alpha(UI.green, 0.45)}`,
            bgcolor: alpha(UI.green, 0.06),
            px: 2,
            py: 2.5,
            textAlign: 'center',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `radial-gradient(ellipse at top, ${alpha(UI.green, 0.16)} 0%, transparent 70%)`,
              pointerEvents: 'none',
            },
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <MonoLabel color={UI.green}>Recipient gets in Somalia</MonoLabel>
            <Typography
              sx={{
                mt: 0.75,
                fontSize: { xs: 42, sm: 46 },
                fontWeight: 900,
                letterSpacing: '-0.03em',
                lineHeight: 1.05,
                background: UI.gradientText,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              {formatUsd(quote.recipientUsd)}
            </Typography>
            <Box sx={{ mt: 1.5 }}>{renderProviders()}</Box>
          </Box>
        </Box>
      </Stack>
    );
  }

  function renderSendButton() {
    if (!onSendNow) return null;
    return (
      <Box
        component="button"
        type="button"
        onClick={onSendNow}
        sx={{
          width: '100%',
          height: 52,
          border: 0,
          borderRadius: 6,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 15,
          fontWeight: 800,
          color: '#0A0B08',
          background: UI.gradient,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          '&:hover': { filter: 'brightness(1.05)' },
        }}
      >
        Send now
        <Iconify icon="solar:arrow-right-linear" width={18} />
      </Box>
    );
  }

  function renderFootnote() {
    let leftoverText = '';
    if (quote && !quote.tooSmall) {
      leftoverText = ` About ${formatUsd(quote.leftoverUsd)} stays in your wallet as a gas reserve.`;
    }

    return (
      <Stack direction="row" spacing={0.75} alignItems="flex-start">
        <Iconify
          icon="solar:info-circle-linear"
          width={14}
          sx={{ color: UI.textFaint, mt: '2px', flexShrink: 0 }}
        />
        <Typography
          sx={{ fontFamily: UI.mono, fontSize: 10.5, color: UI.textFaint, lineHeight: 1.6 }}
        >
          Estimate for buying ETH in MetaMask by card or Google Pay. The final amount depends on the
          ETH price when your purchase completes.{leftoverText}
        </Typography>
      </Stack>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: paperSx }}
      slotProps={{
        backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' } },
      }}
    >
      <Stack spacing={2.25} sx={{ p: { xs: 2.5, sm: 3 } }}>
        {renderHeader()}
        {renderCurrencyToggle()}
        {renderAmountField()}
        {renderResult()}
        {renderSendButton()}
        {renderFootnote()}
      </Stack>
    </Dialog>
  );
}

CalculatorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSendNow: PropTypes.func,
};
